<template>
  <div class="my-12 md:my-20 container max-w-screen-xl">
    <h2 v-if="heading" class="px-4 fluid-xl leading-tight font-medium" v-html="heading"></h2>
    <div v-if="description" class="copy-text px-4 mt-4 fluid-lg font-light leading-normal" v-html="description"></div>
    <ExerciseCard :exercise="exerciseWithOverwrites" />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ExerciseCard from '@/components/exercises/ExerciseCard';
import { validObjectId } from '@/utils/algolia';
import * as Sentry from '@sentry/vue';
import { useSchema } from '@/composables/useSchema';
import { generateExerciseHowToSchema } from './Exercise.schema';
import { useAppStore } from '@/stores/app';

// Props
const props = defineProps({
  exerciseObjectId: {
    type: String,
    default: null,
  },
  exerciseData: {
    type: Object,
    default: null,
  },
  heading: {
    type: String,
    default: null,
  },
  description: {
    type: String,
    default: null,
  },
  title: {
    type: String,
    default: null,
  },
  shortDescription: {
    type: String,
    default: null,
  },
  longDescription: {
    type: String,
    default: null,
  },
  practiceLabelKey: {
    type: String,
    default: 'none',
  },
  practiceValue: {
    type: String,
    default: null,
  },
});

// Composables
const { t } = useI18n();
const { registerComponentSchema, EXERCISE_GRAPH_SCHEMA } = useSchema();
const { $algoliaWorkouts, $localeRootPath } = useNuxtApp();
const appStore = useAppStore();

// Reactive state
const exercise = ref(null);

const exerciseWithOverwrites = computed(() => {
  if (!exercise.value) return null;

  const mergePropKeys = ['title', 'longDescription', 'shortDescription', 'practiceValue', 'practiceLabel'];
  const exerciseData = { ...exercise.value };

  for (const key of mergePropKeys) {
    if (props[key]) {
      exerciseData[key] = props[key];
    }
  }

  return exerciseData;
});

// Generate HowTo schema for this exercise
const generateExerciseSchema = () => {
  if (!exerciseWithOverwrites.value) {
    return;
  }

  const exerciseData = exerciseWithOverwrites.value;
  const siteIdentity = appStore.siteIdentity || {};

  try {
    const schema = generateExerciseHowToSchema(exerciseData, siteIdentity);

    if (schema) {
      registerComponentSchema(schema, EXERCISE_GRAPH_SCHEMA, true);
    }
  } catch (error) {
    console.error('Error in generateExerciseHowToSchema:', error.message);
  }
};

// Initialize exercise data and generate schema during SSR
if (props.exerciseData) {
  exercise.value = props.exerciseData;
  generateExerciseSchema();
} else if (validObjectId(props.exerciseObjectId)) {
  try {
    const fetchedExercise = await $algoliaWorkouts.fetchExerciseById(props.exerciseObjectId);
    exercise.value = fetchedExercise;
    generateExerciseSchema();
  } catch (error) {
    console.log('Could not fetch exercise during SSR:', error.message);
  }
}
</script>
